<!-- Wrapper acts as the visible .form-control -->
<div
  class="phi-wrap form-control"
  [class.phi-focused]="focused()"
  [class.is-invalid]="isInvalid || innerError()"
>
  <!-- -- Flag + Dial trigger -- -->
  <button
    type="button"
    class="phi-trigger"
    [attr.aria-label]="'Country: ' + country().name + ', ' + country().dialCode"
    [attr.aria-expanded]="open()"
    aria-haspopup="listbox"
    [disabled]="disabled()"
    (click)="toggleOpen($event)"
  >
    <img
      class="phi-flag-img"
      [src]="
        'https://flagcdn.com/20x15/' + country().iso2.toLowerCase() + '.png'
      "
      [srcset]="
        'https://flagcdn.com/40x30/' + country().iso2.toLowerCase() + '.png 2x'
      "
      [alt]="country().name"
      width="20"
      height="15"
    />
    <span class="phi-code">{{ country().dialCode }}</span>
    <svg
      class="phi-arrow"
      [class.phi-arrow--up]="open()"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <path
        d="M1 3.5l4 4 4-4"
        stroke="currentColor"
        stroke-width="1.6"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </button>

  <span class="phi-sep" aria-hidden="true"></span>

  <!-- -- Number input -- -->
  <input
    #numInput
    class="phi-num"
    type="tel"
    inputmode="tel"
    autocomplete="tel"
    [placeholder]="getPlaceholder()"
    [value]="display()"
    [disabled]="disabled()"
    [attr.aria-label]="'Phone number for ' + country().name"
    (focus)="focused.set(true)"
    (blur)="onBlur()"
    (keydown)="onKey($event)"
    (input)="onInput($event)"
    (paste)="onPaste($event)"
  />

  <!-- Clear -->
  @if (digits().length && !disabled()) {
    <button
      type="button"
      class="phi-clear"
      aria-label="Clear"
      (mousedown)="$event.preventDefault()"
      (click)="clear()"
      (keydown.enter)="clear()"
    >
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <path
          d="M1 1l10 10M11 1L1 11"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
    </button>
  }

  <!-- -- Dropdown -- -->
  @if (open()) {
    <div
      class="phi-drop"
      (click)="$event.stopPropagation()"
      (keydown.enter)="$event.stopPropagation()"
    >
      <!-- Search -->
      <div class="phi-search-row">
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          aria-hidden="true"
          class="phi-search-ico"
        >
          <circle
            cx="5.5"
            cy="5.5"
            r="4"
            stroke="currentColor"
            stroke-width="1.4"
            fill="none"
          />
          <path
            d="M9 9l3.5 3.5"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
          />
        </svg>
        <input
          #searchEl
          class="phi-search"
          type="text"
          [formControl]="searchCtrl"
          placeholder="Search country or +code"
          autocomplete="off"
          (click)="$event.stopPropagation()"
          (keydown)="onSearchKey($event)"
          aria-label="Search countries"
        />
        @if (searchCtrl.value) {
          <button
            class="phi-search-clr"
            type="button"
            (mousedown)="$event.preventDefault()"
            (click)="searchCtrl.setValue(''); searchEl.focus()"
            (keydown.enter)="searchCtrl.setValue(''); searchEl.focus()"
          >
            <svg width="10" height="10" viewBox="0 0 10 10">
              <path
                d="M1 1l8 8M9 1L1 9"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </button>
        }
      </div>

      <!-- List -->
      <ul class="phi-list">
        @if (!filtered().length) {
          <li class="phi-empty">No countries found</li>
        }
        @for (c of filtered(); track trackCountry(c)) {
          <li
            class="phi-item"
            [class.phi-item--sel]="
              c.iso2 === country().iso2 && c.priority === country().priority
            "
            [attr.aria-selected]="c.iso2 === country().iso2"
            (mousedown)="$event.preventDefault()"
            (click)="pick(c); $event.stopPropagation()"
            (keydown.enter)="pick(c)"
            (keydown.space)="pick(c)"
          >
            <img
              class="phi-item-flag-img"
              [src]="
                'https://flagcdn.com/20x15/' + c.iso2.toLowerCase() + '.png'
              "
              [srcset]="
                'https://flagcdn.com/40x30/' + c.iso2.toLowerCase() + '.png 2x'
              "
              [alt]="c.name"
              width="20"
              height="15"
            />
            <span class="phi-item-name">{{ c.name }}</span>
            <span class="phi-item-code">{{ c.dialCode }}</span>
            @if (c.iso2 === country().iso2) {
              <svg
                class="phi-item-chk"
                width="13"
                height="13"
                viewBox="0 0 13 13"
              >
                <path
                  d="M1.5 7l3.5 3.5 6-7"
                  stroke="currentColor"
                  stroke-width="1.8"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            }
          </li>
        }
      </ul>
    </div>
  }
</div>

<!-- Validation messages (Bootstrap-compatible) -->
@if (isInvalid && parentErrors) {
  <div class="invalid-feedback d-block">
    @if (parentErrors["required"]) {
      <span class="text-danger">Phone number is required</span>
    } @else if (
      parentErrors["phoneTooShort"] ||
      parentErrors["phoneTooLong"] ||
      parentErrors["pattern"] ||
      parentErrors["minlength"] ||
      innerError()
    ) {
      <span class="text-danger"
        >Please enter a valid phone number for {{ country().name }}</span
      >
    }
  </div>
}
